import {useRef, useState, KeyboardEvent, useEffect} from 'react'
import {X} from 'lucide-react'
import {cn} from '@/lib/utils'
import {Badge} from '@/components/ui/badge'

export interface TagSuggestion {
    code: string
    label: string
}

interface TagInputProps {
    values: string[]
    onAdd: (value: string) => void
    onRemove: (index: number) => void
    canRemoveItem?: (index: number) => boolean
    pending: string
    onPendingChange: (value: string) => void
    placeholder?: string
    disabled?: boolean
    error?: string | null
    hint?: string | null
    maxItems?: number
    testId?: string
    /** Autocomplete suggestions — filtered as user types */
    suggestions?: TagSuggestion[]
}

export function TagInput({
    values,
    onAdd,
    onRemove,
    canRemoveItem = () => true,
    pending,
    onPendingChange,
    placeholder,
    disabled,
    error,
    hint,
    maxItems,
    testId,
    suggestions,
}: TagInputProps) {
    const inputRef = useRef<HTMLInputElement>(null)
    const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const atMax = maxItems != null && values.length >= maxItems
    const [showSuggestions, setShowSuggestions] = useState(false)

    useEffect(() => () => {
        if (blurTimeoutRef.current !== null) clearTimeout(blurTimeoutRef.current)
    }, [])

    // Filter suggestions: match pending text, exclude already-added values
    const filteredSuggestions = suggestions?.filter(s =>
        !values.includes(s.code) &&
        (pending.length === 0 || s.label.toLowerCase().includes(pending.toLowerCase()) || s.code.toLowerCase().includes(pending.toLowerCase()))
    ) ?? []

    const hasSuggestions = suggestions && suggestions.length > 0

    function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            e.preventDefault()
            const trimmed = pending.trim()
            if (trimmed && !atMax) {
                // If suggestions exist, match against them (use code)
                if (hasSuggestions) {
                    const match = suggestions!.find(s =>
                        s.label.toLowerCase() === trimmed.toLowerCase() || s.code.toLowerCase() === trimmed.toLowerCase()
                    )
                    if (match) onAdd(match.code)
                } else {
                    onAdd(trimmed)
                }
            }
        } else if (e.key === 'Backspace' && pending === '' && values.length > 0) {
            e.preventDefault()
            onRemove(values.length - 1)
        } else if (e.key === 'Escape') {
            setShowSuggestions(false)
        }
    }

    function handleSelectSuggestion(code: string) {
        if (atMax) return
        onAdd(code)
        // Keep the dropdown open so the user can pick additional values without
        // clicking out and back in. If this selection fills the last allowed slot,
        // the dropdown hides itself via the atMax guard on next render.
        inputRef.current?.focus()
    }

    function handleBlur() {
        if (blurTimeoutRef.current !== null) clearTimeout(blurTimeoutRef.current)
        blurTimeoutRef.current = setTimeout(() => {
            setShowSuggestions(false)
            blurTimeoutRef.current = null
        }, 150)
    }

    // Resolve display label for a tag value (show label instead of code if suggestions available)
    function tagLabel(val: string): string {
        if (!suggestions) return val
        const match = suggestions.find(s => s.code === val)
        return match ? match.label : val
    }

    return (
        <div className="relative space-y-1">
            <div
                className={cn(
                    'flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border bg-transparent px-3 py-1.5 text-sm shadow-xs transition-colors',
                    'focus-within:outline-none focus-within:ring-1 focus-within:ring-ring',
                    error ? 'border-destructive' : 'border-input',
                    disabled && 'cursor-not-allowed opacity-50',
                )}
                onClick={() => inputRef.current?.focus()}
            >
                {values.map((val, idx) => (
                    <Badge key={idx} variant="outline" className="gap-1 pr-1 shrink-0" role="listitem">
                        {tagLabel(val)}
                        {!disabled && canRemoveItem(idx) && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onRemove(idx) }}
                                className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                                data-testid={`${testId}-remove-${idx}`}
                            >
                                <X className="h-3 w-3"/>
                            </button>
                        )}
                    </Badge>
                ))}
                {!disabled && !atMax && (
                    <input
                        ref={inputRef}
                        value={pending}
                        onChange={(e) => { onPendingChange(e.target.value); setShowSuggestions(true) }}
                        onKeyDown={handleKeyDown}
                        onFocus={() => hasSuggestions && setShowSuggestions(true)}
                        onBlur={handleBlur}
                        placeholder={values.length === 0 ? placeholder : undefined}
                        className="flex-1 min-w-[80px] bg-transparent outline-none placeholder:text-muted-foreground"
                        data-testid={`${testId}-input`}
                    />
                )}
            </div>

            {/* Autocomplete dropdown */}
            {showSuggestions && filteredSuggestions.length > 0 && !atMax && (
                <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
                    <ul className="max-h-48 overflow-y-auto py-1">
                        {filteredSuggestions.map(s => (
                            <li key={s.code}>
                                <button
                                    type="button"
                                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleSelectSuggestion(s.code)}
                                    data-testid={`${testId}-suggestion-${s.code}`}
                                >
                                    {s.label}
                                    {s.code !== s.label && (
                                        <span className="ml-2 text-xs text-muted-foreground">{s.code}</span>
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {error && <p className="text-xs text-destructive" data-testid="validation-error">{error}</p>}
            {hint && !error && <p className="text-xs text-muted-foreground" data-testid="validation-hint">{hint}</p>}
        </div>
    )
}
